import { DeepPartial } from '@chakra-ui/react';
import {
  IActionReference,
  IAvailableWorkflowActions,
  IPeerReviewRevisionAssessment,
  IRoleType,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { IFormSchema } from '@publishing-engine/domain/models/form-schema';
import { IEarlyAssessmentManagementView } from '@publishing-engine/domain/views';
import {
  IGetActiveRevisionResponse,
  IGetSubmissionFieldsResponse,
  IGetSubmissionWorkflowResponse
} from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchActiveRevision,
  fetchEarlyAssessment,
  fetchEarlyAssessmentFormDraft,
  fetchFormSchema,
  fetchOrganizationAndJournalsInformation,
  fetchReviewExtensionDeadline,
  fetchSubmissionContext,
  fetchSubmissionFields,
  fetchSubmissionWorkflow,
  IFetchOrganizationAndJournalsInfo
} from 'src/api/queries';
import { IExpertFirstAssessment } from 'src/pages/MyTask/Adjudications/adjudicationType';
import Loading from 'src/pages/Status/Loading';
import NotFound from 'src/pages/Status/NotFound';
import Forbidden from 'src/pages/Status/Forbidden';
import { isForbiddenError } from 'src/utils/queryClient';

type AssessmentContextData = {
  fieldsResponse: IGetSubmissionFieldsResponse;
  earlyAssessment: IEarlyAssessmentManagementView;
  workflowResponse: IGetSubmissionWorkflowResponse;
  availableActions: IAvailableWorkflowActions;
  earlyAssessmentFormSchema: {
    documentSchema: IFormSchema;
  };
  revisionFormSchema: {
    documentSchema: IFormSchema;
  };
  invalidateAssessmentContextData: () => void;
  submissionContext: ISubmissionContext;
  organizationData: IFetchOrganizationAndJournalsInfo;
  organizationId: string;
  submissionId: string;
  firstAssessmentFormDraft: DeepPartial<IExpertFirstAssessment> | undefined;
  revisionFormDraft: DeepPartial<IPeerReviewRevisionAssessment> | undefined;
  activeRevisionData?: IGetActiveRevisionResponse | null;
  assessmentExtensionDeadline:
  | {
    currentState: string;
    currentDeadline: string;
    nextDeadline: string;
    deadlineExtensionInDays: number;
  }
  | undefined;
  refetchAssessment: () => void;
  hasSubmittedAssessmentInCurrentRound: () => boolean;
  isInviteRequestAccepted: () => boolean;
};

/**
 *
 * @param workflowResponse
 * @param currentState
 * @returns Available actions on Review Workflow State
 */
function getAvailableWorkflowActions(
  workflowResponse: IGetSubmissionWorkflowResponse,
  currentState: string,
  roleContext: string
): IAvailableWorkflowActions {
  const actions =
    workflowResponse.articleWorkflow.transitionGraph.review.states[
      currentState || ''
    ]?.actions || [];

  return {
    contains: function (
      actionReference: IActionReference['name'],
      withRole?: boolean
    ) {
      return withRole
        ? actions.some(
          a => a.name === actionReference && a.roleType === roleContext
        )
        : actions.some(a => a.name === actionReference);
    }
  };
}

export const AssessmentDataContext = createContext<
  AssessmentContextData | undefined
>(undefined);

export const useAssessmentData = (): AssessmentContextData => {
  const context = useContext(AssessmentDataContext);
  if (!context) {
    throw new Error(
      'useAssessmentData must be used within a AssessmentDataProvider'
    );
  }
  return context;
};

export function getAssessmentRequestContextFromRoute() {
  if (document.location.href.match('assessments')) {
    const { assessmentId } = useParams();

    if (!assessmentId) {
      throw new Error('Unable to retrieve assessmentId from params');
    }

    return {
      roleContext: 'expert',
      assessmentId
    };
  }

  throw new Error('Unable to retrieve assessmentId from params');
}

const AssessmentDataProvider = ({ children }: { children: JSX.Element }) => {
  const { roleContext, assessmentId } = getAssessmentRequestContextFromRoute();
  const queryClient = useQueryClient();

  const earlyAssessmentKey = ['early-assessment-data', { assessmentId }];
  const {
    data: assessmentData,
    isLoading: isLoadingAssessment,
    isError: isErrorAssessment,
    error: errorAssessment,
    refetch: refetchAssessment
  } = useQuery({
    queryKey: earlyAssessmentKey,
    queryFn: () => fetchEarlyAssessment(assessmentId)
  });

  const submissionFieldsQueryKey = [
    'submission-fields-assessment',
    assessmentId
  ];
  const {
    data: submissionFields,
    isLoading: isLoadingSubmissionFields,
    isError: isErrorFields
  } = useQuery({
    queryKey: submissionFieldsQueryKey,
    queryFn: () =>
      assessmentData?.caseData?.submission?.id
        ? fetchSubmissionFields(
          assessmentData?.caseData?.submission?.id,
          roleContext as IRoleType
        )
        : null,
    enabled: !!assessmentData?.caseData?.submission?.id,
    retry: 0
  });

  const {
    data: submissionContext,
    isLoading: isLoadingContext,
    isError: isErrorContext
  } = useQuery({
    queryKey: ['submission-context', assessmentData?.submissionContextId],
    queryFn: () =>
      assessmentData?.submissionContextId
        ? fetchSubmissionContext(assessmentData?.submissionContextId)
        : null
  });

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization
  } = useQuery({
    queryKey: [
      'organization',
      assessmentData?.submissionContextId,
      submissionContext?.organizationId
    ],
    queryFn: () =>
      submissionContext?.organizationId
        ? fetchOrganizationAndJournalsInformation(
          submissionContext?.organizationId
        )
        : null
  });

  const workflowQueryKey = [
    'submission-workflow',
    assessmentData?.caseData?.submission?.id
  ];
  const {
    data: workflowData,
    isLoading: isLoadingWorkflow,
    isError: isErrorWorkflow
  } = useQuery({
    queryKey: workflowQueryKey,
    queryFn: () =>
      assessmentData?.caseData?.submission?.id
        ? fetchSubmissionWorkflow(
          assessmentData?.caseData?.submission?.id,
          roleContext as IRoleType
        )
        : null,
    enabled: !!assessmentData?.caseData?.submission?.id,
    retry: 0
  });

  const firstAssessmentSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.externalEarlyAssessment;

  const revisionAssessmentSchemaId =
    workflowData?.articleWorkflow?.formSchemas?.reviewerRevisionAssessment;

  const {
    data: assessmentFormSchema,
    isLoading: isLoadingReviewSchema,
    isError: isErrorReviewSchema
  } = useQuery({
    queryKey: firstAssessmentSchemaId
      ? [
        'review-context',
        assessmentData?.submissionContextId,
        'schema',
        firstAssessmentSchemaId
      ]
      : [],
    queryFn: () =>
      assessmentData?.submissionContextId && firstAssessmentSchemaId
        ? fetchFormSchema(
          assessmentData?.submissionContextId,
          firstAssessmentSchemaId
        )
        : undefined
  });

  const {
    data: assessmentExtensionDeadline,
    isLoading: isLoadingAssessmentExtensionDeadline,
    isError: isErrorAssessmentExtensionDeadline
  } = useQuery({
    queryKey: ['assessment-extension-deadline', assessmentData?.id],
    queryFn: () =>
      assessmentData?.id
        ? fetchReviewExtensionDeadline(assessmentData?.id, 'expert')
        : null
  });

  const {
    data: revisionFormSchema,
    isLoading: isLoadingRevisionSchema,
    isError: isErrorRevisionSchema
  } = useQuery({
    queryKey: revisionAssessmentSchemaId
      ? [
        'review-context',
        assessmentData?.submissionContextId,
        'schema',
        revisionAssessmentSchemaId
      ]
      : [],
    queryFn: () =>
      assessmentData?.submissionContextId && revisionAssessmentSchemaId
        ? fetchFormSchema(
          assessmentData?.submissionContextId,
          revisionAssessmentSchemaId
        )
        : undefined
  });

  const {
    data: firstAssessmentFormDraft,
    isLoading: isLoadingFirstAssessmentFormDraft
  } = useQuery({
    queryKey: [
      'form-draft',
      assessmentId,
      'early_assessment',
      'externalEarlyAssessment'
    ],
    queryFn: () =>
      assessmentId
        ? fetchEarlyAssessmentFormDraft(
          assessmentId,
          'early_assessment',
          'externalEarlyAssessment'
        )
        : undefined
  });

  const { data: revisionFormDraft, isLoading: isLoadingRevisionFormDraft } =
    useQuery({
      queryKey: [
        'form-draft',
        assessmentId,
        'early_assessment',
        'reviewerRevisionAssessment'
      ],
      queryFn: () =>
        assessmentId
          ? fetchEarlyAssessmentFormDraft(
            assessmentId,
            'early_assessment',
            'reviewerRevisionAssessment'
          )
          : undefined
    });

  const { data: activeRevisionData, isLoading: isLoadingActiveRevision } =
    useQuery({
      queryKey: ['active-revision', assessmentData?.caseData?.submission?.id],
      queryFn: () =>
        assessmentData?.caseData?.submission?.id
          ? fetchActiveRevision(
            assessmentData?.caseData?.submission?.id,
            roleContext as IRoleType
          )
          : null,
      enabled: !!assessmentData?.caseData?.submission?.id,
      retry: 0
    });

  if (
    isLoadingAssessment ||
    isLoadingSubmissionFields ||
    isLoadingWorkflow ||
    isLoadingContext ||
    isLoadingOrganization ||
    isLoadingReviewSchema ||
    isLoadingRevisionSchema ||
    isLoadingFirstAssessmentFormDraft ||
    isLoadingActiveRevision ||
    isLoadingRevisionFormDraft ||
    isLoadingAssessmentExtensionDeadline
  ) {
    return <Loading />;
  }

  const isError =
    isErrorFields ||
    isErrorContext ||
    isErrorOrganization ||
    isErrorWorkflow ||
    isErrorAssessment ||
    isErrorReviewSchema ||
    isErrorRevisionSchema ||
    isErrorAssessmentExtensionDeadline;

  const isDataEmpty =
    !assessmentData ||
    !submissionFields ||
    !submissionContext ||
    !organizationData ||
    !assessmentFormSchema ||
    !workflowData ||
    !revisionFormSchema ||
    !assessmentExtensionDeadline;

  if (isError || isDataEmpty) {
    return isForbiddenError(errorAssessment) ? <Forbidden /> : <NotFound />;
  }

  const {
    caseData: {
      submission: { id: submissionId }
    }
  } = assessmentData;

  const invalidateAssessmentContext = () => {
    queryClient.invalidateQueries({ queryKey: earlyAssessmentKey });
    queryClient.invalidateQueries({ queryKey: submissionFieldsQueryKey });
  };

  const hasSubmittedAssessmentInCurrentRound = () => {
    if (activeRevisionData) {
      return !!assessmentData.revisionAssessment;
    }

    return !!assessmentData.caseData.firstAssessment;
  };

  const isAssessmentRequestAccepted = () => {
    return assessmentData.caseData.acceptance === 'accept';
  };

  return (
    <AssessmentDataContext.Provider
      value={{
        earlyAssessment: assessmentData,
        fieldsResponse: submissionFields,
        submissionId,
        workflowResponse: workflowData,
        availableActions: getAvailableWorkflowActions(
          workflowData,
          assessmentData.workflow.currentState,
          roleContext
        ),
        organizationData: organizationData,
        submissionContext: submissionContext,
        organizationId: submissionContext.organizationId,
        earlyAssessmentFormSchema: assessmentFormSchema,
        revisionFormSchema: revisionFormSchema,
        firstAssessmentFormDraft:
          firstAssessmentFormDraft?.data as DeepPartial<IExpertFirstAssessment>,
        revisionFormDraft:
          revisionFormDraft?.data as DeepPartial<IPeerReviewRevisionAssessment>,
        activeRevisionData: activeRevisionData,
        invalidateAssessmentContextData: invalidateAssessmentContext,
        assessmentExtensionDeadline,
        refetchAssessment,
        hasSubmittedAssessmentInCurrentRound,
        isInviteRequestAccepted: isAssessmentRequestAccepted
      }}
    >
      {children}
    </AssessmentDataContext.Provider>
  );
};

export default AssessmentDataProvider;
